// import { getFilterObject } from '@pages/HousingList/utils';
import { getFilterObject } from '@/common';
import { COUNTRY_LIST } from '@/constants';
import { TFunction } from './i18n';
// import { Namespace, TFunction } from 'i18next';

export enum OfferType {
  SELL = 'SELL',
  RENT = 'RENT',
}

export enum SearchTabs {
  LIST = 'LIST',
  TILE = 'TILE',
  MAP = 'MAP',
}

export interface OfferTypeObject {
  code: OfferType;
  title: string;
  count: number;
}

export type CountryCode = keyof typeof COUNTRY_LIST;

export enum FormOfAddress {
  MR = 'Herr',
  MS = 'Frau',
  NO = 'Ohne Anrede',
  COMPANY = 'Unternehmen',
}

export enum DesiredContactTime {
  ANY = 'Jederzeit erreichbar',
  MORNING = 'vormittags',
  AFTERNOON = 'nachmittags',
  EVENING = 'abends',
}

export interface Category {
  code: string;
  title: string;
  count: number;
}

export interface Equipment {
  code: string;
  title: string;
  count: number;
}

export interface Unit {
  code: string;
  title: string;
  count: number;
  admunitIds?: string[];
  children?: Unit[];
}

export interface Equip {
  code: string;
  title: string;
}

export type UnitItem = Unit & { children: (Unit & { children: Unit[] })[] };
export type UnitsMultilevelList = UnitItem[];

export interface RangesDimensions {
  roomNumber: number[];
  price: number[];
  rentPrice: number[];
  area: number[];
}

export interface SendReportRequestData {
  requestBody: {
    codes: string[];
    objectLink: string;
    email?: string;
    message?: string;
  };
}

export interface MediaFile {
  link: string;
  type: string;
  descriptionDefault: string;
  changeDate: number;
  descriptions: object;
}

export interface ImageWithSizes {
  imageFullUrl: string;
  imageMidUrl: string;
  imageTmbUrl: string;
}

export type Sorting =
  | 'MODIFICATION_DATE'
  | 'CREATION_DATE'
  | 'SELL_PRICE_ASC'
  | 'SELL_PRICE_DESC'
  | 'RENT_PRICE_ASC'
  | 'RENT_PRICE_DESC'
  | 'PRICE_ASC'
  | 'PRICE_DESC'
  | 'AREA_ASC'
  | 'AREA_DESC'
  | 'POSTCODE_ASC'
  | 'POSTCODE_DESC';

export interface SortObject {
  currentPageIndex: number;
  countries: Array<CountryCode>;
  division: Array<string>;
  districts: Array<string>;
  categories: Array<string>;
  customEquips: Array<string>;
  offerType: typeof OfferType;
  priceFrom: number | null;
  priceTo: number | null;
  rentPriceFrom: number | null;
  rentPriceTo: number | null;
  spaceFrom: number | null;
  spaceTo: number | null;
  groundSpaceFrom: number | null;
  groundSpaceTo: number | null;
  roomNumberFrom: number | null;
  roomNumberTo: number | null;
  sorting: Sorting;
}

export interface ImmoProjectDataRo {
  title: string | null;
  startDate: string | null;
  endDate: string | null;
  areaFrom: number | null;
  areaTo: number | null;
  roomFrom: number | null;
  roomTo: number | null;
  rentFrom: number | null;
  rentTo: number | null;
  priceFrom: number | null;
  priceTo: number | null;
  immoNumFound: number | null;
  immoObjects: IHousingObjectResponse[] | null;
}

interface IHousingObject {
  id: string;
  thumbnail: string;
  hasVideo: boolean;
  hasPdf: boolean;
  hasDocument: boolean;
  hasExtMedia: boolean;
  extMediaLinks: [string];
  equips: [string];
  customEquips: [string];
  buildYear: number;
  buildYearIsApproximate: boolean;
  title: string;
  deTitle: string;
  enTitle: string;
  ruTitle: string;
  itTitle: string;
  description: string;
  defaultDescription: string;
  deDescription: string;
  enDescription: string;
  skDcription: string;
  itDescription: string;
  ruDescription: string;
  htDescription: string;
  plDescription: string;
  frDescription: string;
  esDescription: string;
  country: string;
  propertyNumber: string;
  groundAreaFrom: number;
  groundAreaTo: number;
  livingAreaFrom: number;
  livingAreaTo: number;
  areaFrom: number;
  areaTo: number;
  areaIsApproximate: boolean;
  roomsNumber: number;
  bathsNumber: number;
  wcsNumber: number;
  revenuePercent: number;
  livingAreaIsApproximate: boolean;
  groundAreaIsApproximate: boolean;
  operationalCosts: number;
  balconiesNumber: number;
  balconySpace: number;
  terracesNumber: number;
  terraceSpace: number;
  cellarSurface: number;
  obstructedSpace: number;
  state: string;
  energyIndex: number;
  powerConsumptionClass: string;
  energyEfficiencyFactor: number;
  energyClassFactor: string;
  createDate: Date;
  modifyDate: Date;
  divisionId: string;
  division: string;
  districtId: string;
  district: string;
  heating: string;
  cellar: string;
  garage: number;
  parking: number;
  floorsNumber: number;
  floor: string;
  noiseLoad: string;
  furniture: string;
  orientation: string;
  category: number;
  categoryName: string;
  subcategory1: string;
  subcategory2: string;
  subcategory3: string;
  objectNumber: number;
  officeNumber: number;
  projectId: string;
  objectSearchNumber: string;
  offerTypes: [string];
  officeId: string;
  brokerId: string;
  isSell: boolean;
  isRent: boolean;
  isLeased: boolean;
  officeAreaTo: number;
  officeAreaFrom: number;
  officeAreaIsApproximate: boolean;
  officeAreaPresumably: number;
  groundAreaPresumably: number;
  storageAreaTo: number;
  storageAreaFrom: number;
  storageAreaIsApproximate: boolean;
  storageAreaPresumably: number;
  productionAreaTo: number;
  productionAreaFrom: number;
  productionAreaIsApproximate: boolean;
  productionAreaPresumably: number;
  usableAreaTo: number;
  usableAreaFrom: number;
  usableAreaIsApproximate: boolean;
  achievableUsefulAreaTo: number;
  achievableUsefulAreaFrom: number;
  achievableUsefulAreaIsApproximate: boolean;
  pavedAreaTo: number;
  pavedAreaFrom: number;
  pavedAreaIsApproximate: boolean;
  loggiaArea: number;
  fencedArea: number;
  isCellarInTheFlat: string;
  isHalfARoom: boolean;
  hasPersonnelElevator: string;
  constructionLicense: string;
  obtainableFrom: Date;
  obtainableFromText: string;
  obtainableFromTextDe: string;
  obtainableFromTextEn: string;
  obtainableFromTextIt: string;
  obtainableFromTextRu: string;
  hasSidingTrack: string;
  hasLoadingArea: string;
  storageHeight: number;
  storageAsAProduction: string;
  hasElevatorForFreights: string;
  numberOfLoggias: number;
  rentTermFrom: number;
  rentTermTo: number;
  outbuildings: number;
  hasCargoPlatform: string;
  operationalAndHeatingCostsTotal: number;
  heatingAndHotWaterCostsTotal: number;
  score: number;
  companies: [string];
  thumbnailHeight: string;
  thumbnailWidth: string;
  phone: string;
  email: string;
  branchName: string;
  branchPlz: string;
  branchOrt: string;
  branchStreet: string;
}

interface EquipGroup {
  code: number | null;
  title: string;
  items: Equip[];
}

interface CustomEquip {
  code: string;
  title: string;
}

export interface BrokerData {
  id: number;
  officeId: number;
  name: string;
  imageFullUrl: string;
  officeName: string;
  brokerOfficeName: string;
  address: {
    postcode: string;
    place: string;
    street: string;
    streetNumber: string;
    streetAddendum: string;
    streetNumber1: string;
    streetAddendum1: string;
  };
  contactName: string;
  contactEmail: string;
  contactSite: string;
  contactPhone: {
    number: string;
  };
  contactMobile: {
    number: string;
  };
  officeSlug: string;
  officeDirectLink?: string;
  brokerMobile: string;
  whatsappDisabled: boolean;
}

export interface IBrokerOfficePriceRange {
  from?: number | null;
  to?: number | null;
  currency?: string | null;
}

export interface OfficeActiveLocations {
  zipCodes?: string[] | null;
  places?: string[] | null;
  municipalities?: { code: string; name: string }[] | null;
}

export interface IBrokerOfficeStatistics {
  activePropertiesCount?: number | null;
  activeSellCount?: number | null;
  activeRentCount?: number | null;
  activeProjectCount?: number | null;
  lastActiveDate?: string | null;
  regions?: string[] | null;
  activeLocations?: OfficeActiveLocations | null;
  priceRanges?: {
    sale?: IBrokerOfficePriceRange | null;
    rent?: IBrokerOfficePriceRange | null;
  } | null;
}

// Response of GET /office/details/propileya/{idOrDirectLink} (OfficeDetailedRo).
export interface IBrokerOfficeObjectResponse {
  id: number;
  slug: string;
  directLink?: string | null;
  name: string;
  displayName?: string | null;
  propileyaMember?: boolean | null;
  location?: {
    countryCode?: string | null;
    zip?: string | null;
    town?: string | null;
    district?: string | null;
    addressLine?: string | null;
    fullAddress?: string | null;
    coordinates?: { lat?: number | null; lng?: number | null } | null;
  } | null;
  contacts?: {
    website?: string | null;
    email?: string | null;
    phone?: {
      countryCode?: string | null;
      prefix?: string | null;
      number?: string | null;
      directDialNumber?: string | null;
      formatted?: string | null;
      telUri?: string | null;
    } | null;
  } | null;
  branding?: {
    logo?: string | null;
    bigLogo?: string | null;
  } | null;
  content?: {
    aboutUs?: string | null;
    founded?: number | null;
  } | null;
  statistics?: IBrokerOfficeStatistics | null;
  meta?: {
    updatedAt?: string | null;
  } | null;
}

// Response item of GET /details/propileya/{objectId}/adjacent-locations (AdjacentLocationLinkRo).
export interface IAdjacentLocationLink {
  name: string;
  label: string;
  slug: string;
  href: string;
}

// Response of GET /details/propileya/{objectId}/adjacent-locations (AdjacentLocationsRo).
export interface IAdjacentLocationsResponse {
  municipalities?: IAdjacentLocationLink[] | null;
  districts?: IAdjacentLocationLink[] | null;
}

export interface ProjectRootRo {
  projectId: number;
  title: string | null;
  slug: string | null;
}

export interface IHousingObjectResponse {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
  immoData: IHousingObject;
  equips: [CustomEquip];
  customEquips: [CustomEquip];
  equipmentGroups: [EquipGroup];
  mapPointType: 'EXACTLY_POINT' | 'CIRCLE' | 'NONE' | null;
  project: boolean;
  projectId: number | null;
  projectData: ImmoProjectDataRo | null;
  projectRoot: ProjectRootRo | null;
  slug: string;
  categoryId: number | null;
  prices: {
    sellPrice: string;
    grossRent: string;
    monthlyCosts: string;
    sellPriceNumeric: number | null;
    grossRentNumeric: number | null;
    monthlyCostsNumeric: number | null;
    yield: string;
    transferFee: string;
    deposit: string;
    buyerCommission: string;
    rentCommission: string;
    operationalCostsTotal: string;
    heatingCostsTotal: string;
    hotWaterCostsTotal: string;
    garbageCostsTotal: string;
    channelCostsTotal: string;
    gasCostsTotal: string;
    insuranceCostsTotal: string;
    otherOperationalCostsTotal: string;
    heatingInclHotWaterCostsTotal: string;
    heatingInclHotWaterCosts: string;
    repairFundsCostsTotal: string;
    parkingCostsTotal: string;
  };
  mapCircleRadius: string;
  heatingCode: string;
  heatingTitle: string;
  stateCode: string;
  stateTitle: string;
  furnitureCode: string;
  furnitureTitle: string;
  constructionLicenseCode: string;
  constructionLicenseTitle: string;
  categoryNameCode: string;
  categoryNameTitle: string;
  noiseLoadCode: string;
  noiseLoadTitle: string;
  cellarCode: string;
  cellarTitle: string;
  address: string;
  postcode: string;
  place: string;
  currencyCode: string;
  latitude: number;
  longitude: number;
  powerConsumptionClass: string;
  energyEfficiencyFactor: number;
  id: string;
  active: boolean;
  inWatchlist: boolean;
  hasInfoRequest: boolean;
  title: string;
  description: string;
  rent: boolean;
  sell: boolean;
  leasing: boolean;
  objectNumber: number;
  objectSearchNumber: string;
  imageFullUrl: string;
  imageMidUrl: string;
  imageTmbUrl: string;
  imageList: Array<{
    imageFullUrl: string;
    imageMidUrl: string;
    imageTmbUrl: string;
  }>;
  countryTitle: string;
  divisionTitle: string;
  districtTitle: string;
  categoryTitle: string;
  energyIndex: number;
  otherDetails: {
    buildYear: string;
    state: string;
    heating: string;
    cellar: string;
    roomNumber: string;
    lift: string;
    garage: string;
    parking: string;
    bathsNumber: string;
    balconiesNumber: string;
    constructionLicense: string;
    obtainableFromText: string | null;
    obtainableFromDate: string | null;
    floorsNumber: string;
    floor: string;
    sidingTrack: string;
    categoryNam: string;
    hasLoadingArea: string;
    noiseLoad: string;
    storageHeight: string;
    storageAsAProduction: string;
    hasElevatorForFreights: string;
    numberOfLoggias: string;
    rentTerm: string;
    furniture: string;
    outbuildings: string;
    orientation: string;
    streetFront1: string;
    streetFront2: string;
    terracesNumbe: string;
    hasCargoPlatform: string;
    wcsNumber: string;
    energyIndex: string;
    powerConsumptionClass: string;
    energyEfficiencyFacto: string;
    energyClassFactor: string;
  };
  squares: {
    areaDetailed: string;
    groundArea: string;
    officeArea: string;
    officeAreaPresumably: string;
    groundAreaPresumably: string;
    storageArea: string;
    storageAreaPresumably: string;
    productionArea: string;
    productionAreaPresumably: string;
    usableArea: string;
    achievableUsefulArea: string;
    pavedArea: string;
    livingArea: string;
    terraceSpace: string;
    balconySpace: string;
    loggiaArea: string;
    cellarSurface: string;
    obstructedSpace: string;
    fencedArea: string;
  };
  media: {
    [x: string]: MediaFile[];
  };
  extMediaLinks: [
    {
      link: string;
      type: string;
      descriptionDefault: string;
      changeDate: number;
      descriptions: {
        additionalProp1: string;
        additionalProp2: string;
        additionalProp3: string;
      };
    },
  ];
  youtubeLinks: [string];
  brokerData: BrokerData;
}

export interface SearchFilterStructureRo {
  originalSellPriceMinimum: number;
  originalSellPriceMaximum: number;
  rentPriceMinimum: number;
  rentPriceMaximum: number;
  areaFromMinimum: number;
  areaFromMaximum: number;
  groundAreaFromMinimum: number;
  groundAreaFromMaximum: number;
  roomsNumberMinimum: number;
  roomsNumberMaximum: number;
  energyIndexMinimum: number;
  energyIndexMaximum: number;
  energyEfficiencyFactorMinimum: number;
  energyEfficiencyFactorMaximum: number;
  countries: Array<Unit>;
  federalStates: Array<Unit>;
  districts: Array<Unit>;
  categories: Array<Category>;
  equipment: Array<Equipment>;
  powerConsumptionClasses: Array<Category>;
  energyClassFactors: Array<Category>;
}

export interface AdmunitNameRo {
  id: string;
  name: string;
  parentName: string;
}

export interface IHousingListResponse {
  start: number;
  numFound: number;
  keywords: null | string;
  objects: Array<IHousingObjectResponse>;
  filterData: {
    offerTypRentTotalCount: number;
    offerTypSellTotalCount: number;
    originalSellPriceMinimum: number;
    originalSellPriceMaximum: number;
    rentPriceMinimum: number;
    rentPriceMaximum: number;
    areaFromMinimum: number;
    areaFromMaximum: number;
    groundAreaFromMinimum: number;
    groundAreaFromMaximum: number;
    roomsNumberMinimum: number;
    roomsNumberMaximum: number;
    powerConsumptionClasses: Array<Category>;
    energyClassFactors: Array<Category>;
    countries: Array<Unit>;
    federalStates: Array<Unit>;
    districts: Array<Unit>;
    categories: Array<Category>;
    equipment: Array<Equipment>;
    admunits?: Array<AdmunitNameRo>;
  };
}

export interface InquiryRequestData {
  objectId: string;
  inquiryObj: BrokerRequest;
  // t: TFunction<Namespace, undefined>;
}

// Body of POST /inquiry/office/propileya/{officeId} (OfficeInquiryRequestRo)
// without `captchaToken` — the mutation appends it.
export interface IOfficeInquiryRequest {
  name: string;
  surname: string;
  email: string;
  phone: string;
  comment: string;
  requestCall: boolean;
  gdprAccepted: boolean;
  sendEmail: boolean;
  sendWhatsapp: boolean;
}

export interface IOfficeInquiryResult {
  whatsappMessageId?: string | null;
  emailSent?: boolean | null;
  message?: string | null;
}

export interface OfficeInquiryRequestData {
  officeId: number;
  inquiryObj: IOfficeInquiryRequest;
}

export interface SearchProfile {
  id: number;
  name: string;
  subscribe: boolean;
  viewType: SearchTabs;
  filters: ReturnType<typeof getFilterObject>;
}

export type PredefinedSearchFilters = ReturnType<typeof getFilterObject>;

export type WatchListResponse = {
  numFound: number;
  objects: IHousingObjectResponse[];
};

export interface AppVersionResponse {
  version: string;
  time: Date;
  branch: string;
}

export interface UserMeResponse {
  version: string;
  time: Date;
  branch: string;
}

export interface CreateSearchProfile {
  name: string;
  subscribe: boolean;
  // filters: ReturnType<typeof getFilterObject>;
}

export interface SearchProfileRequestData {
  profileObj: CreateSearchProfile;
  // t: TFunction<Namespace, undefined>;
}

export interface UpdateSearchProfileObj {
  name?: string;
  subscribe?: boolean;
  id: number;
}

export interface UpdateSearchProfileRequestData {
  updateObj: UpdateSearchProfileObj;
  // t: TFunction<Namespace, undefined>;
}

export type FieldDef = {
  label: string | null;
  isEquip: boolean;
  key: string;
  keys?: string[];
  format?: (value: string, t: TFunction) => void;
};

export type SearchListFilters =
  | {
      itemsPerPage?: number;
      currentPageIndex?: number;
      offerType?: OfferType;
      categories?: string[];
      customEquips?: string[];
      countries?: string[];
      division?: string[];
      districts?: string[];
      spaceFrom?: number | null;
      spaceTo?: number | null;
      roomNumberFrom?: number | null;
      roomNumberTo?: number | null;
      priceFrom?: number | null;
      priceTo?: number | null;
      rentPriceFrom?: number | null;
      rentPriceTo?: number | null;
      powerConsumptionClasses?: string[];
      energyClassFactors?: string[];
      locations?: { type: string; code: string; name: string; postcode?: string }[] | null;
      sorting?: Sorting | null;
      officeId?: number | null;
      admunitIds?: string[];
      mapArea?: {
        lonLatGeometry?: [number, number][];
        lonLatLeftBottom?: [number, number];
        lonLatRightTop?: [number, number];
      } | null;
      keywords?: string;
      project?: boolean;
      projectId?: number | null;
      itemsPerProject?: number;
    }
  | undefined;

export interface BrokerRequest {
  title: string;
  name: string;
  surname: string;
  zipCode: string;
  street: string;
  city: string;
  email: string;
  phone: string;
  comment: string;
  desiredContactTime: string;
  requestVisit: boolean;
  requestDetails: boolean;
  requestCall: boolean;
  requestSimilarObjects: boolean;
  requestSendCopyToMe: boolean;
  gdprAccepted: boolean;
  sendEmail: boolean;
  sendWhatsapp: boolean;
  createdAt: Date;
  formOfAddress: string;
}

export type BrokerRequestsResponse = BrokerRequest[];

export type WhatsappMessageStatus = 'WAITING' | 'SENT' | 'FAILED';

export type WhatsappMessageStatusResponse = {
  status: WhatsappMessageStatus;
};

export interface ErrorResponse {
  requestId: string;
  timestamp: string;
  cause: string;
}

// Response of GET /private/broker_admin/office_statistics (OfficeAdminStatisticsRo).
export interface OfficeAdminStatisticsRo {
  activeForSaleCount?: number | null;
  activeForRentCount?: number | null;
  alleImmobilienInfoRequestsCount?: number | null;
  disabledForSaleCount?: number | null;
  disabledForRentCount?: number | null;
  infoRequestsCount?: number | null;
  watchlistImmosCount?: number | null;
  wlasDeliveredInfoRequestsCount?: number | null;
  wlasReceivedInfoRequestsCount?: number | null;
}

export interface OfficeProfileRo {
  name?: string;
  directLink?: string;
  email?: string;
  phone?: string;
  site?: string;
  address?: string;
  region?: string;
  founded?: number;
  aboutUs?: string;
  smallLogo?: string;
  bigLogo?: string;
  activeLocations?: OfficeActiveLocations | null;
}

export interface OfficeProfileUpdateRo {
  name?: string;
  directLink?: string;
  email?: string;
  phone?: string;
  site?: string;
  address?: string;
  region?: string;
  founded?: number;
  aboutUs?: string;
}
